import { Component, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import StsA360RegStep2UsernamePostModel from '../../../models/sts-a360-reg-step2-username-post.model';
import { StsNotificationType } from '../../../models/sts-notification-type';
import { StsNotification } from '../../../models/sts-notification.model';
import { StsPortalStatus } from '../../../models/sts-portal-status';
import { StsRegexBluePrintModel } from '../../../models/sts-regex-blue-print.model';
import { StsTenant } from '../../../models/sts-tenant.model';
import { StsA360RegServicesService } from '../../../services/sts-a360-reg-services.service';
import { StsPortalStatusService } from '../../../services/sts-portal-status.service';
import { StsTenantService } from '../../../services/sts-tenant.service';
import { StsTfaRegServiceService } from '../../../services/sts-tfa-reg-service.service';
import {StsUserService} from "../../../services/sts-user.service";
import {StsUser} from "../../../models/sts-user.model";
import StsA360NotificationTokenModel from 'app/models/sts-a360-notification-token.model';


@Component({
  selector: 'app-account-reg-collect-email',
  templateUrl: './account-reg-collect-email.component.html',
  styleUrls: ['./account-reg-collect-email.component.scss']
})
export class AccountRegCollectEmailComponent implements OnInit {

  public model: StsA360RegStep2UsernamePostModel = new StsA360RegStep2UsernamePostModel();
  public errors: StsNotification[] = [];
  public signUpForm: FormGroup;
  public tenantInfo: StsTenant = new StsTenant();
  public triggerValidateEmitter: EventEmitter<void> = new EventEmitter<void>();
  public emailIsValid = true;
  public phoneIsValid = true;
  public submitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  /** Guards against double submission while a back-end call (e.g. LexisNexis) is in flight */
  public isSubmitting = false;
  public isLogIn: boolean = false;
  public isLogin2FA: boolean = false;
  public displayEmail: boolean = false;
  public displayPhone: boolean = false;
  /** Indicates we are returning to this screen after MFA validation (LexisNexis flow) */
  public isPostMfa: boolean = false;

  message: StsA360NotificationTokenModel;

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

  constructor(private tfa: StsTfaRegServiceService,
              private regService: StsA360RegServicesService,
              private userService: StsUserService,
              private portalStatusService: StsPortalStatusService,
              private route: ActivatedRoute,
              private tenantService: StsTenantService,
              public fb: FormBuilder,
              private tntInfo: StsTenantService,
              private router: Router) {

    this.route.params.subscribe(params => {
      this.isLogIn = params['isLogIn'] === 'true';
      this.isLogin2FA = params['isLogin2FA'] === 'true';
      this.isPostMfa = params['isPostMfa'] === 'true';
      this.tenantService.getTenantSource().subscribe(tenant => {
        this.tenantInfo = tenant;

        this.portalStatusService.getPortalStatus().subscribe(
          (portalStatus: StsPortalStatus) => {
            let isSelfRegFlow = portalStatus.isSelfRegFlow;

            if (!params['id']) {
              console.log('User attempting to execute \'set username\' without permission! Redirecting to login.');
              this.router.navigateByUrl('/account/signin');
              return;
            }
            this.model = new StsA360RegStep2UsernamePostModel();
            this.model.id = params['id'];
            
            // Determine which fields to display based on LexisNexis and MFA state
            if (this.message.EnableLexisNexisIntegration) {
              if (this.isPostMfa) {
                // Post-MFA: only collect phone number now
                this.displayPhone = true;
                this.displayEmail = false;
              } else if (this.tenantInfo.isTfaEnabled) {
                // Pre-MFA with TFA enabled
                if (!this.message.Email && this.message.PersonalEmail) {
                  // No CRM email on file — email was just collected on pick-user-name screen:
                  // show phone field so it can be collected before MFA
                  this.displayPhone = true;
                  this.displayEmail = false;
                } else {
                  // Email was on file (via CRM): go to MFA first, collect phone post-MFA
                  this.displayPhone = false;
                  this.displayEmail = false;
                }
              } else {
                // LexisNexis on but TFA disabled: collect both email (if needed) and phone on this screen
                this.displayPhone = true;
                if (!this.message.PersonalEmail && !this.message.Email) {
                  this.displayEmail = true;
                }
              }
            } else {
              // Non-LexisNexis: original behavior
              if (!this.message.PersonalEmail && !this.message.Email) {
                this.displayEmail = true;
              }
            }
            // If LexisNexis + MFA enabled and email already on file (used as User ID),
            // no fields need to be collected pre-MFA. Skip this form entirely:
            // trigger MFA via email and redirect to the validation component.
            if (this.message.EnableLexisNexisIntegration && !this.isPostMfa
                && !this.displayEmail && !this.displayPhone
                && this.tenantInfo.isTfaEnabled) {
              let channel = 'email';
              let address = this.message.PersonalEmail || this.message.Email;
              this.message.PreferredChannel = channel;
              this.message.PostMfaRedirect = '/account/collect/email/' + this.model.id + '/false/false;isPostMfa=true';
              this.regService.changeMessage(this.message);

              this.tfa.sendValidationEmail(channel, address).subscribe(a => {
                if (a.success) {
                  this.router.navigateByUrl('/account/validate/email/' + this.model.id);
                } else {
                  let e = new StsNotification();
                  e.code = a.stsCode || 'sts-failed-tfa-email';
                  e.level = StsNotificationType.Error;
                  e.description = 'An error occurred while validating your email';
                  this.errors = [e];
                }
              });
              return;
            }

            if(this.message.Phone) {
              let fc = this.signUpForm.controls['phoneInput'] as FormControl;
              fc.setValue(this.message.Phone);
            }
          });
      });

      this.signUpForm = this.fb.group({

        emailInput: new FormControl('',
          {
            validators: [
              (c: FormControl) => {
                if (!this.displayEmail) return null; 
                if (!c.value) {
                  return {
                    emailRequired: {
                      valid: false
                    }
                  };
                }
                let re = atob(this.tenantInfo.usefulPatterns['email'] + '');
                re = re.replace(/(^\\A)|(\\Z$)/g, '');
                this.emailIsValid = (new RegExp(re)).test(c.value);
                return (new RegExp(re)).test(c.value) ? null : {
                  validateEmail: {
                    valid: false
                  }
                };
              },
              ] ,

            updateOn: 'submit'
          }),

          phoneInput: new FormControl('',
          {
            validators: [
              (c: FormControl) => {
                if (!this.displayPhone) return null; 
                if (!c.value) {
                  return {
                    phoneRequired: {
                      valid: false,
                    },
                  };
                } 
                let re = atob(this.tenantInfo.usefulPatterns["phone"] + "");
                re = re.replace(/(^\\A)|(\\Z$)/g, "");
                this.phoneIsValid = new RegExp(re).test(c.value);
                return new RegExp(re).test(c.value)
                  ? null
                  : {
                      validatePhone: {
                        valid: false,
                      },
                    };               
              },
              ] ,

            updateOn: 'submit'
          })
      });     

    });

  }

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

  public triggerValidation(v) {

    this.signUpForm.controls['emailInput'].updateValueAndValidity();
    this.signUpForm.controls['phoneInput'].updateValueAndValidity();
  }

  public resetFields() {

    this.signUpForm.controls['emailInput'].reset();
    this.signUpForm.controls['phoneInput'].reset();
  }

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

  public gotoHome(): void {
    this.router.navigateByUrl('/');
  }

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

  getReType(re: string): StsRegexBluePrintModel {
    let tmp = new StsRegexBluePrintModel();
    re = re.replace(/(^\\A)|(\\Z$)/g, '');
    tmp.regexp = new RegExp(btoa(re));
    tmp.model = this.model;
    return tmp;
  }

  gotoNextStep() {
    
  }

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

  /**
   * Single place that drives both the re-entrancy guard and the button's
   * disabled/spinner state, so the Next button can never fire a second
   * back-end call while one is already in flight.
   */
  private setSubmitting(inFlight: boolean): void {
    this.isSubmitting = inFlight;
    this.submitter.emit(inFlight);
  }

  private failSubmit(code: string, description: string): void {
    this.setSubmitting(false);
    let e = new StsNotification();
    e.code = code;
    e.level = StsNotificationType.Error;
    e.description = description;
    this.errors = [e];
  }

  onSubmit(): void {
    // Re-entrancy guard: ignore repeated clicks / implicit form submits
    // while a request is still outstanding.
    if (this.isSubmitting) {
      return;
    }
    this.setSubmitting(false);
    this.triggerValidation('');
    if (this.signUpForm.valid) {
      this.setSubmitting(true);
      this.errors = [];
      let n = new StsA360RegStep2UsernamePostModel();
      n.id = this.model.id;
      let user = new StsUser();
      user.id = n.id;
      
      if(this.displayEmail){
        n.email = this.signUpForm.controls['emailInput'].value;
        this.message.PersonalEmail = n.email;
        user.hasEmailChanged = true;        
        user.personalEmail = n.email;
      }
      else{
        n.email = this.message.PersonalEmail;
        user.hasEmailChanged = false;        
        user.personalEmail = n.email;
      }

      if(this.displayPhone){
        n.mobilePhone = this.signUpForm.controls['phoneInput'].value;
        this.message.Phone = n.mobilePhone;
      }
      else{
        n.mobilePhone = this.message.Phone;
      }      
      
      this.regService.changeMessage(this.message);

      // =================================================================
      // POST-MFA LexisNexis flow: phone just collected, now register
      // =================================================================
      if (this.isPostMfa && this.message.EnableLexisNexisIntegration && !this.isLogIn) {
        n.username = this.message.UserName;
        n.email = this.message.PersonalEmail || this.message.Email;
        this.regService.postStep2Answers(n).subscribe(rs => {
          // postStep2Answers swallows HTTP failures and emits null
          if (!rs) {
            this.failSubmit('sts-generic-error', 'An error has occurred!');
            return;
          }
          this.setSubmitting(false);
          if (rs.success) {
            this.router.navigate(['/account/password/new', { id: user.id }]);
          } else {
            let e = new StsNotification();
            e.code = rs['sts-code'] || rs.stsCode;
            e.level = StsNotificationType.Error;
            e.description = 'Submitted data contains errors';
            this.errors = [e];
          }
        }, () => this.failSubmit('sts-generic-error', 'An error has occurred!'));
        return;
      }

      //If Login we update the EmailAAddress. If Registeration workflow, we register the user.
      //Registration needs to happen here as we do not have any other endpoint to update the email address in the CRM
      if(!this.isLogIn){

        // =================================================================
        // PRE-MFA LexisNexis flow: email collected, skip registration,
        // send MFA via email, then come back for phone + registration
        // =================================================================
        if (this.message.EnableLexisNexisIntegration) {
          // NOTE: do NOT release the submit guard here - every path below issues
          // at least one back-end call (LexisNexis account_creation can take
          // several seconds). The guard is released in each callback instead.
          if (this.tenantInfo.isTfaEnabled) {
            let channel = 'email';
            let address = n.email || this.message.PersonalEmail || this.message.Email;

            this.message.PreferredChannel = channel;

            if (!this.message.Email) {
              // No email on file: phone was just collected on this screen.
              // Call postStep2Answers to register with both email and phone,
              // then trigger MFA. No PostMfaRedirect needed — go straight to password after MFA.
              n.username = this.message.UserName;
              n.email = this.message.PersonalEmail;
              this.regService.changeMessage(this.message);

              this.regService.postStep2Answers(n).subscribe(rs => {
                // postStep2Answers swallows HTTP failures and emits null
                if (!rs) {
                  this.failSubmit('sts-generic-error', 'An error has occurred!');
                  return;
                }
                if (rs.success) {
                  this.tfa.sendValidationEmail(channel, address).subscribe(a => {
                    this.setSubmitting(false);
                    if (a.success) {
                      this.router.navigateByUrl('/account/validate/email/' + user.id);
                    } else {
                      let e = new StsNotification();
                      e.code = a.stsCode || 'sts-failed-tfa-email';
                      e.level = StsNotificationType.Error;
                      e.description = 'An error occurred while validating your email';
                      this.errors = [e];
                    }
                  }, () => this.failSubmit('sts-failed-tfa-email', 'An error occurred while validating your email'));
                } else {
                  this.setSubmitting(false);
                  let e = new StsNotification();
                  e.code = rs['sts-code'] || rs.stsCode;
                  e.level = StsNotificationType.Error;
                  e.description = 'Submitted data contains errors';
                  this.errors = [e];
                }
              }, () => this.failSubmit('sts-generic-error', 'An error has occurred!'));
            } else {
              // Email was on file: redirect back for phone collection after MFA
              this.message.PostMfaRedirect = '/account/collect/email/' + user.id + '/false/false;isPostMfa=true';
              this.regService.changeMessage(this.message);

              this.tfa.sendValidationEmail(channel, address).subscribe(a => {
                this.setSubmitting(false);
                if (a.success) {
                  this.router.navigateByUrl('/account/validate/email/' + user.id);
                } else {
                  let e = new StsNotification();
                  e.code = a.stsCode || 'sts-failed-tfa-email';
                  e.level = StsNotificationType.Error;
                  e.description = 'An error occurred while validating your email';
                  this.errors = [e];
                }
              }, () => this.failSubmit('sts-failed-tfa-email', 'An error occurred while validating your email'));
            }
          } else {
            // No TFA but LexisNexis on — phone was already collected on this screen,
            // so do standard registration and proceed to password
            n.username = this.message.UserName;
            n.email = this.message.PersonalEmail || this.message.Email;
            this.regService.postStep2Answers(n).subscribe(rs => {
              // postStep2Answers swallows HTTP failures and emits null
              if (!rs) {
                this.failSubmit('sts-generic-error', 'An error has occurred!');
                return;
              }
              this.setSubmitting(false);
              if (rs.success) {
                this.router.navigate(['/account/password/new', { id: user.id }]);
              } else {
                let e = new StsNotification();
                e.code = rs['sts-code'] || rs.stsCode;
                e.level = StsNotificationType.Error;
                e.description = 'Submitted data contains errors';
                this.errors = [e];
              }
            }, () => this.failSubmit('sts-generic-error', 'An error has occurred!'));
          }
          return;
        }

        // =================================================================
        // Standard (non-LexisNexis) Registration Flow
        // =================================================================
        n.username = this.message.UserName;
        this.regService.postStep2Answers(n).subscribe(rs => {
          // postStep2Answers swallows HTTP failures and emits null
          if (!rs) {
            this.failSubmit('sts-generic-error', 'An error has occurred!');
            return;
          }
          this.setSubmitting(false);
          if (rs.success) {        
            //tenant has 2fa
            if (this.tenantInfo.isTfaEnabled) {
              let channel = this.tenantInfo.tfaPreferredMode;
              //2 channels are defined for the tenant
              if (this.tenantInfo.tfaDeliveryChannels && this.tenantInfo.tfaDeliveryChannels.length > 1 && this.tenantInfo.tfaDeliveryChannels.includes('SMS')) {
                this.router.navigateByUrl('/account/reg/sel-comm/' + user.id);
                return;
              }
              else {
                if (this.tenantInfo.tfaDeliveryChannels) {
                  channel = this.tenantInfo.tfaDeliveryChannels.includes('SMS') ? 'phone' : channel;
                }
                this.message.PreferredChannel = channel;
                this.regService.changeMessage(this.message);
              }

              let address = channel === 'phone' ? this.message.Phone : (n.email ? n.email : this.message.PersonalEmail);
              
              //If we have changed the email address then we need to update the message service as well
              this.message.PersonalEmail = n.email;
              this.regService.changeMessage(this.message);

              this.tfa.sendValidationEmail(channel, address).subscribe(a => {
                  if (a.success) {
                    this.router.navigateByUrl('/account/validate/email/' + user.id);
                  } else {
                    if (this.message.PreferredChannel === 'email' || this.message.PreferredChannel === 'personalemail') {
                      let e = new StsNotification();
                      e.code =  a.stsCode ||'sts-failed-tfa-email';
                      e.level = StsNotificationType.Error;
                      e.description = 'An error occurred while validating your email';
                      this.errors = [e];
                    }
                    else {
                      if (this.message.PreferredChannel == "phone") {
                        let e = new StsNotification();
                        e.code =  a.stsCode || 'sts-failed-tfa-email-phone';
                        e.level = StsNotificationType.Error;
                        e.description = 'an error occured while validating your phone';
                        this.errors = [e];
                      }
                      else {
                        let e = new StsNotification();
                        e.code = rs['sts-code'] || a.stsCode;
                        e.level = StsNotificationType.Error;
                        e.description = 'Submitted data contains errors';
                        this.errors = [e];
                      }
                    }
                  }
                });
            } else {
              this.router.navigate(['/account/password/new', { id: user.id }]);
            }

          }else{
            let e = new StsNotification();
            e.code = rs['sts-code'] || rs.stsCode;
            e.level = StsNotificationType.Error;
            e.description = 'Submitted data contains errors';

            if (e.code === 'sts-invalid-email'
              && (this.message.PreferredChannel === 'email' || this.message.PreferredChannel === 'personalemail')
              && !this.message.Email
              && !this.message.PersonalEmail
              && this.message.Phone
            ) {
              e.code = "STS_LOGIN_FAILED";
            }

            this.errors = [e];
          }
          
        }, () => this.failSubmit('sts-generic-error', 'An error has occurred!'));

      }
      else{
        //Login Flow
        //This Email will not be updated in the CRM
        this.userService.updateEmailAddress(user).subscribe(rs => {
          if (rs.success) {
            // Keep the button disabled: both branches below either issue a
            // further request or navigate away from this screen.
            if(this.isLogin2FA){
              this.redirectTo2FAVerification();
            }
            else {
              document.location.href = '/';
            }
          }
          else{
            this.setSubmitting(false);
            let e = new StsNotification();
            e.code = rs['sts-code'] || rs.stsCode;
            e.level = StsNotificationType.Error;
            e.description = 'submitted data contains errors';
            this.errors = [e];
          }

        }, () => this.failSubmit('sts-generic-error', 'An error has occurred!'));
      }    
    } else {
      this.setSubmitting(false);
      let e = new StsNotification();
      e.code = 'sts-correct-email-errors';
      e.level = StsNotificationType.Error;
      e.description = 'submitted data contains errors';
      this.errors = [e];
    }
  }

  private redirectTo2FAVerification(): void {
    this.userService.getCommunicationChannels().subscribe(result => {
      if (result) {
        let deliveryChannels = this.tenantInfo.tfaDeliveryChannels;
        let communicationChannels = result.communicationChannels.map(channel => channel.Type);
        // channels available to the user
        let availableChannels = communicationChannels.filter(channel => deliveryChannels.includes(channel));

        // many delivery channels available
        if (availableChannels.length > 1 && availableChannels.includes('SMS')) {
          this.router.navigateByUrl('/account/2fa/sel-comm');
        } else if (availableChannels.includes('SMS')) { // only sms delivery available
          this.router.navigateByUrl('/account/2fa/validate/phone');
        } else if (availableChannels.includes('EMAIL')) { // only email delivery available
          this.router.navigateByUrl('/account/2fa/validate/email');
        } else { // no channels available
          // Dead end - re-enable the button so the user is not locked out
          this.setSubmitting(false);
          this.notify('sts-no-communication-channels', 'You have no communication channels on file', StsNotificationType.Error);
        }
      } else {
        this.setSubmitting(false);
      }
    }, () => this.failSubmit('sts-generic-error', 'An error has occurred!'));
  }

  private notify(code: string, description: string, type: StsNotificationType): void {
    let notification = new StsNotification();
    notification.code = code;
    notification.description = description;
    notification.level = type;

    this.errors.push(notification);
  }


  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@

  ngOnInit() {
    this.regService.currentMessage.subscribe(message => this.message = message);
  }

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@





}

